"use client";

import React, { useState, useEffect } from "react";

export interface InsuranceCalculatorProps {
  maxRate: number;
  minRate: number;
  onCalculatedRate?: (rate: number) => void;
}

interface CalculationResult {
  recommendedRate: number;
  sessionsPerYear: number;
  sessionFrequency: string;
  monthlySpend: number;
}

export function InsuranceCalculator({ maxRate, minRate, onCalculatedRate }: InsuranceCalculatorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInsurance, setHasInsurance] = useState<boolean | null>(null);
  const [inputs, setInputs] = useState({
    annualInsurance: '',
    monthlyBudget: ''
  });
  const [result, setResult] = useState<CalculationResult | null>(null);

  useEffect(() => {
    if (inputs.annualInsurance || inputs.monthlyBudget) {
      calculateRate();
    }
  }, [inputs]);

  const calculateRate = () => {
    const annualInsurance = parseFloat(inputs.annualInsurance) || 0;
    const monthlyPersonal = parseFloat(inputs.monthlyBudget) || 0;
    
    // Calculate total annual budget
    const totalAnnualBudget = annualInsurance + (monthlyPersonal * 12);
    
    if (totalAnnualBudget === 0) {
      setResult(null);
      return;
    }
    
    // Calculate optimal session frequency and rate
    // Try different frequencies to find the best fit
    const frequencies = [
      { name: 'Weekly sessions', sessions: 52, label: 'weekly' },
      { name: 'Bi-weekly sessions', sessions: 26, label: 'biweekly' },
      { name: 'Monthly sessions', sessions: 12, label: 'monthly' }
    ];
    
    let bestOption = null;
    
    for (const freq of frequencies) {
      const ratePerSession = Math.round(totalAnnualBudget / freq.sessions);
      
      if (ratePerSession >= minRate && ratePerSession <= maxRate) {
        bestOption = {
          recommendedRate: ratePerSession,
          sessionsPerYear: freq.sessions,
          sessionFrequency: freq.name,
          monthlySpend: Math.round(totalAnnualBudget / 12)
        };
        break;
      }
    }
    
    // If no frequency works within bounds, calculate with the max/min rate
    if (!bestOption) {
      if (totalAnnualBudget / 52 > maxRate) {
        // Budget is too high even for weekly sessions at max rate
        bestOption = {
          recommendedRate: maxRate,
          sessionsPerYear: Math.floor(totalAnnualBudget / maxRate),
          sessionFrequency: `${Math.round(totalAnnualBudget / maxRate / 12)} sessions per month`,
          monthlySpend: Math.round(totalAnnualBudget / 12)
        };
      } else {
        // Budget is too low even for monthly sessions at min rate
        bestOption = {
          recommendedRate: minRate,
          sessionsPerYear: Math.floor(totalAnnualBudget / minRate),
          sessionFrequency: `${Math.floor(totalAnnualBudget / minRate)} sessions per year`,
          monthlySpend: Math.round(totalAnnualBudget / 12)
        };
      }
    }
    
    setResult(bestOption);
  };

  const handleInputChange = (field: string, value: string) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
      >
        Need help finding a sustainable rate? Use our calculator →
      </button>
    );
  }

  return (
    <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-gray-900">Find Your Sustainable Rate</h3>
        <button
          onClick={() => {
            setIsOpen(false);
            setHasInsurance(null);
            setInputs({ annualInsurance: '', monthlyBudget: '' });
            setResult(null);
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-4">
        {/* Insurance Question */}
        {hasInsurance === null && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              Do you have insurance coverage for therapy?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setHasInsurance(true)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
              >
                Yes
              </button>
              <button
                onClick={() => setHasInsurance(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
              >
                No
              </button>
            </div>
          </div>
        )}

        {/* Insurance Amount */}
        {hasInsurance === true && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Annual insurance coverage for therapy ($)
            </label>
            <input
              type="number"
              value={inputs.annualInsurance}
              onChange={(e) => handleInputChange('annualInsurance', e.target.value)}
              placeholder="e.g., 1000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Check your insurance plan for your annual mental health benefit
            </p>
          </div>
        )}

        {/* Monthly Budget */}
        {hasInsurance !== null && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly budget you can set aside for therapy ($)
            </label>
            <input
              type="number"
              value={inputs.monthlyBudget}
              onChange={(e) => handleInputChange('monthlyBudget', e.target.value)}
              placeholder="e.g., 200"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              What you can comfortably afford each month from your personal budget
            </p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-4 p-4 bg-primary-50 rounded-lg space-y-3">
            <div>
              <p className="text-sm text-gray-700">Based on your budget, we recommend:</p>
              <p className="text-2xl font-semibold text-primary-700 mt-1">
                ${result.recommendedRate} per session
              </p>
            </div>
            
            <div className="text-sm text-gray-700 space-y-1">
              <p>✓ {result.sessionFrequency} ({result.sessionsPerYear} sessions/year)</p>
              <p>✓ Monthly spend: ${result.monthlySpend}</p>
              <p>✓ Sustainable for the full year</p>
            </div>

            <button
              onClick={() => {
                onCalculatedRate?.(result.recommendedRate);
                setIsOpen(false);
                setHasInsurance(null);
                setInputs({ annualInsurance: '', monthlyBudget: '' });
                setResult(null);
              }}
              className="w-full mt-3 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Use this rate
            </button>
          </div>
        )}
      </div>
    </div>
  );
}